import type { ContentPreviewObject } from '../../../lib/flotiq-client/types/api-response';

import { useStore } from '@tanstack/react-form';
import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { postContentObject } from '../../../lib/flotiq-client';
import { updateCTO } from '../../../lib/flotiq-client/api-helpers';
import { parseResponseErrors } from '../../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import LinkButton from '../../../components/LinkButton/LinkButton';

import { useContentObject } from '../../../hooks/api';
import useSectionSave from '../../../hooks/useSectionSave';
import useSelectedSpace from '../../../hooks/useSelectedSpace';
import useToken from '../../../hooks/useToken';

import UserContext from '../../../contexts/UserContext';

import { useAppForm } from '../../../form-hooks/form';
import SpaceSettingsSection from '../SpaceSettingsSection/SpaceSettingsSection';

const testId = 'content-preview-settings';
const CO_ID = 'flotiq.content-publishing';
const SECTION_ID = 'content-preview';

type ContentPreviewData = Pick<ContentPreviewObject, 'base_url' | 'editor_key'>;

interface ContentPreviewSettingsProps {
  contentTypeName: string;
}

const ContentPreviewSettings = ({
  contentTypeName,
}: ContentPreviewSettingsProps) => {
  const { t } = useTranslation();
  const { permissions } = use(UserContext);
  const jwt = useToken();
  const { space, buildUrlWithSpace } = useSelectedSpace();

  const { canUpdate, canCreate, canDelete } = useMemo(
    () => permissions.getCoPermissions(contentTypeName),
    [contentTypeName, permissions],
  );

  const {
    entity: contentObject,
    partialUpdateEntity: partialUpdateContentObject,
    deleteEntity: deleteContentObject,
    reload,
  } = useContentObject(contentTypeName, CO_ID);

  const [showForm, setShowForm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);

  const validationObject = useMemo(
    () =>
      z.object({
        base_url: z.string().min(1, t('SpaceSettings.Errors.MinLength')),
        editor_key: z.string().min(1, t('SpaceSettings.Errors.MinLength')),
      }),
    [t],
  );

  const initialValues = useMemo<ContentPreviewData>(
    () => ({
      base_url: contentObject?.base_url ?? '',
      editor_key: contentObject?.editor_key ?? '',
    }),
    [contentObject],
  );

  const handleDeleteSubmit = useCallback(async () => {
    if (!contentObject) return;
    const { body, status, ok } = await deleteContentObject({
      contentTypeName,
    });

    if (!ok) throw parseResponseErrors(body, status);
  }, [contentObject, contentTypeName, deleteContentObject]);

  const handleUpdateSubmit = useCallback(
    async ({ base_url, editor_key }: ContentPreviewData) => {
      const [, errors, hasErrors] = await updateCTO(
        { base_url, editor_key },
        contentTypeName,
        partialUpdateContentObject,
        t,
        true,
      );
      if (hasErrors) throw errors;
    },
    [contentTypeName, partialUpdateContentObject, t],
  );

  const handleCreateSubmit = useCallback(
    async ({ base_url, editor_key }: ContentPreviewData) => {
      const { body, status, ok } = await postContentObject(jwt, space, {
        contentTypeName,
        id: CO_ID,
        base_url,
        editor_key,
        config: [],
      });

      if (!ok) throw parseResponseErrors(body, status);
      await reload();
    },
    [jwt, space, contentTypeName, reload],
  );

  const form = useAppForm({
    defaultValues: initialValues,
    validators: {
      onChange: validationObject,
    },
    onSubmit: async ({ value }) => {
      if (pendingDelete) {
        await handleDeleteSubmit();
        setPendingDelete(false);
        setShowForm(false);
        form.reset();
        return;
      }

      if (contentObject) {
        await handleUpdateSubmit(value);
        return;
      }

      await handleCreateSubmit(value);
    },
  });

  const isFormDirty = useStore(form.store, (s) => s.isDirty);

  useEffect(() => {
    if (contentObject) {
      setShowForm(true);
      form.reset(initialValues);
    }
  }, [contentObject, form, initialValues]);

  const handleSave = useCallback(async () => {
    if (!showForm && !pendingDelete) return;
    await form.handleSubmit();
  }, [form, showForm, pendingDelete]);

  useSectionSave(SECTION_ID, handleSave, isFormDirty || pendingDelete);

  const handleDelete = useCallback(() => {
    setPendingDelete(true);
    setShowForm(false);
    form.reset(initialValues);
  }, [form, initialValues]);

  const handleConfigure = useCallback(() => {
    setPendingDelete(false);
    setShowForm(true);
  }, []);

  const isEditingDisabled = !canCreate && !canUpdate;

  return (
    <SpaceSettingsSection
      title={t('SpaceSettings.ContentPreview.Title')}
      description={
        <Trans
          i18nKey="SpaceSettings.ContentPreview.Description"
          components={{
            docsLink: (
              <a
                href="https://flotiq.com/docs/panel/SpaceSettings/content-preview/#configuration"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue hover:underline"
              >
                documentation
              </a>
            ),
          }}
        />
      }
    >
      <form.AppForm>
        {showForm && (
          <div className="flex flex-col py-6 md:flex-row gap-6">
            <form.AppField name="base_url">
              {(field) => (
                <field.Input
                  label={t('SpaceSettings.ContentPreview.PageBaseUrl')}
                  disabled={isEditingDisabled}
                  required
                  additionalClasses="flex-1"
                  {...getTestProps(testId, 'base-url', 'testId')}
                />
              )}
            </form.AppField>
            <form.AppField name="editor_key">
              {(field) => (
                <field.Input
                  type={'password'}
                  label={t(
                    'SpaceSettings.ContentPreview.ClientAuthorizationKey',
                  )}
                  disabled={isEditingDisabled}
                  helpText={t(
                    'SpaceSettings.ContentPreview.ClientAuthorizationKeyHelpText',
                  )}
                  required
                  additionalClasses="flex-1"
                  {...getTestProps(testId, 'editor-key', 'testId')}
                />
              )}
            </form.AppField>
          </div>
        )}
      </form.AppForm>

      {showForm ? (
        <div className="flex gap-3">
          <LinkButton link={buildUrlWithSpace('content-type-definitions')}>
            {t('SpaceSettings.ContentPreview.Manage')}
          </LinkButton>
          {canDelete && (
            <Button
              buttonColor="borderless"
              additionalClasses="px-3 hover:underline decoration-blue"
              additionalChildrenClasses="text-blue"
              onClick={handleDelete}
              {...getTestProps(testId, 'delete', 'testId')}
            >
              {t('Global.Delete')}
            </Button>
          )}
        </div>
      ) : (
        canCreate && (
          <div className="mt-6">
            <Button
              onClick={handleConfigure}
              {...getTestProps(testId, 'configure', 'testId')}
            >
              {t('SpaceSettings.ContentPreview.Configure')}
            </Button>
          </div>
        )
      )}
    </SpaceSettingsSection>
  );
};

export default ContentPreviewSettings;
